import { useEffect, useState, useCallback, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { AlertOctagon, Users, ShieldAlert, CheckCircle, Clock, X, Activity } from 'lucide-react';

interface DataBreach {
  id: string;
  title: string;
  description: string;
  impact_assessment: string;
  discovery_date: string;
  breach_start_date?: string;
  breach_end_date?: string;
  affected_data_types: string[];
  affected_users_count: number;
  severity: string;
  status: string;
  notification_sent: boolean;
  notification_sent_at?: string;
  users_notified_count: number;
  regulatory_filing_required: boolean;
  created_at: string;
  // ... other fields omitted for brevity but used in logic
  resolution_details?: string;
  remediation_steps?: string;
  remediation_completed_at?: string;
  regulatory_filing_date?: string;
  regulatory_filing_reference?: string;
  resolved_at?: string;
}

interface DataBreachesResponse {
  breaches: DataBreach[];
  count: number;
}

const getLocalDateInputValue = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

export default function DataBreachesPage() {
  const { t, i18n } = useTranslation('compliance');
  const [breaches, setBreaches] = useState<DataBreach[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadError, setLoadError] = useState('');
  const reportTriggerRef = useRef<HTMLButtonElement | null>(null);
  const wasFormOpenRef = useRef(false);
  const closeReportForm = () => {
    setShowForm(false);
    setError('');
  };

  useEffect(() => {
    if (wasFormOpenRef.current && !showForm) {
      reportTriggerRef.current?.focus();
    }
    wasFormOpenRef.current = showForm;
  }, [showForm]);

  const handleFormKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeReportForm();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = event.currentTarget.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    const first = focusableElements.item(0);
    const last = focusableElements.item(focusableElements.length - 1);

    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    impact_assessment: '',
    discovery_date: getLocalDateInputValue(),
    affected_data_types: [] as string[],
    affected_users_count: 0,
    severity: 'medium',
    regulatory_filing_required: false,
    affected_users_list: '',
  });

  const fetchBreaches = useCallback(async () => {
    setLoading(true);
    const token = getToken();
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (severityFilter) params.append('severity', severityFilter);

    try {
      const res = await fetch(`/api/v1/admin/compliance/data-breaches?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load breach reports');
      const data: DataBreachesResponse = await res.json();
      setBreaches(data.breaches || []);
      setLoadError('');
    } catch (_err) {
      setLoadError(t('dataBreaches.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, t]);

  useEffect(() => {
    fetchBreaches();
  }, [fetchBreaches]);

  const handleCreateBreach = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const token = getToken();
    try {
      const response = await fetch('/api/v1/admin/compliance/data-breaches', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          discovery_date: new Date(formData.discovery_date).toISOString(),
        }),
      });
      if (!response.ok) {
        setError(t('dataBreaches.errors.createFailed'));
        return;
      }
      closeReportForm();
      setSuccess(t('dataBreaches.success.created'));
      await fetchBreaches();
    } catch (_err) {
      setError(t('dataBreaches.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-4xl">
              <ShieldAlert
                className="text-state-danger-light dark:text-state-danger-dark"
                size={32}
              />{' '}
              {t('dataBreaches.title')}
            </h1>
            <p className="mt-2 text-sm leading-6 text-text-muted-light dark:text-text-muted-dark sm:ml-11 sm:text-base">
              {t('dataBreaches.subtitle')}
            </p>
          </div>
          <Button
            onClick={(event) => {
              reportTriggerRef.current = event.currentTarget;
              setError('');
              setShowForm(true);
            }}
            size="lg"
            className="min-h-11 w-full whitespace-nowrap bg-state-danger-light shadow-lg shadow-state-danger-light/20 hover:opacity-90 dark:bg-state-danger-dark dark:shadow-none md:w-auto"
          >
            <AlertOctagon className="mr-2 h-5 w-5" /> {t('dataBreaches.reportIncident')}
          </Button>
        </div>

        {/* Stats / Filters */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-6">
          <Card className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:col-span-3">
            <div className="flex min-w-0 flex-col gap-1">
              <label
                htmlFor="breach-status-filter"
                className="text-xs font-semibold text-text-muted-light dark:text-text-muted-dark"
              >
                {t('dataBreaches.filters.status')}
              </label>
              <select
                id="breach-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="min-h-11 w-full cursor-pointer rounded-lg border border-surface-border-light bg-surface-card-light px-3 text-sm font-medium text-text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-text-primary-dark"
              >
                <option value="">{t('dataBreaches.filters.allStatuses')}</option>
                <option value="investigating">{t('dataBreaches.status.investigating')}</option>
                <option value="notifying_users">{t('dataBreaches.status.notifying_users')}</option>
                <option value="resolved">{t('dataBreaches.status.resolved')}</option>
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <label
                htmlFor="breach-severity-filter"
                className="text-xs font-semibold text-text-muted-light dark:text-text-muted-dark"
              >
                {t('dataBreaches.filters.severity')}
              </label>
              <select
                id="breach-severity-filter"
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="min-h-11 w-full cursor-pointer rounded-lg border border-surface-border-light bg-surface-card-light px-3 text-sm font-medium text-text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-text-primary-dark"
              >
                <option value="">{t('dataBreaches.filters.allSeverities')}</option>
                {['critical', 'high', 'medium', 'low'].map((severity) => (
                  <option key={severity} value={severity}>
                    {t(`dataBreaches.severity.${severity}`)}
                  </option>
                ))}
              </select>
            </div>
          </Card>

          <Card className="flex items-center justify-between p-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
                {t('dataBreaches.activeIncidents')}
              </p>
              <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {loading || loadError
                  ? '—'
                  : breaches.filter((b) => b.status !== 'resolved').length}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-2-light text-state-info-light dark:bg-surface-2-dark dark:text-state-info-dark">
              <Activity size={20} aria-hidden="true" />
            </div>
          </Card>
        </div>

        {(error || success) && !showForm && (
          <div
            role={error ? 'alert' : 'status'}
            aria-live={error ? 'assertive' : 'polite'}
            className={`rounded-lg border p-4 text-sm ${error ? 'border-state-danger-light/20 bg-state-danger-bg-light text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark' : 'border-state-ok-light/20 bg-state-ok-bg-light text-state-ok-light dark:border-state-ok-dark/20 dark:bg-state-ok-bg-dark dark:text-state-ok-dark'}`}
          >
            {error || success}
          </div>
        )}

        {/* Breach List */}
        {loading ? (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-state-danger-light dark:border-state-danger-dark mx-auto mb-4"></div>
            <p
              role="status"
              className="text-xs font-semibold text-text-subtle-light dark:text-text-subtle-dark"
            >
              {t('dataBreaches.loading')}
            </p>
          </div>
        ) : loadError ? (
          <Card className="border-state-danger-light/30 p-8 text-center dark:border-state-danger-dark/30">
            <div role="alert" aria-live="assertive" className="flex flex-col items-center gap-4">
              <p className="text-sm text-state-danger-light dark:text-state-danger-dark">
                {loadError}
              </p>
              <Button variant="secondary" onClick={() => void fetchBreaches()} className="min-h-11">
                {t('dataBreaches.errors.retry')}
              </Button>
            </div>
          </Card>
        ) : breaches.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <ShieldAlert
              size={48}
              className="mx-auto text-text-subtle-light dark:text-text-subtle-dark mb-4"
            />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('dataBreaches.empty.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2">
              {t('dataBreaches.empty.description')}
            </p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {breaches.map((breach) => (
              <Card
                key={breach.id}
                className="p-4 transition-colors hover:border-state-danger-light dark:hover:border-state-danger-dark sm:p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white sm:text-lg">
                        {breach.title}
                      </h3>
                      <Badge
                        variant={toneToBadgeVariant[getStatusTone(breach.severity)]}
                        className="uppercase"
                      >
                        {t(`dataBreaches.severity.${breach.severity}`, breach.severity)}
                      </Badge>
                    </div>
                    <p className="text-sm text-text-muted-light dark:text-text-muted-dark line-clamp-1">
                      {breach.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant={toneToBadgeVariant[getStatusTone(breach.status)]}>
                      {t(`dataBreaches.status.${breach.status}`, breach.status.replace('_', ' '))}
                    </Badge>
                    <p className="text-xs font-mono text-text-subtle-light dark:text-text-subtle-dark mt-2">
                      {new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language).format(
                        new Date(breach.discovery_date)
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-surface-border-light pt-4 text-xs font-semibold text-text-muted-light dark:border-surface-border-dark dark:text-text-muted-dark">
                  <span className="flex items-center gap-2">
                    <Users size={14} />{' '}
                    {t('dataBreaches.details.affected', { count: breach.affected_users_count })}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock size={14} />{' '}
                    {new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    }).format(new Date(breach.created_at))}
                  </span>
                  {breach.notification_sent && (
                    <span className="flex items-center gap-2 text-state-ok-light dark:text-state-ok-dark">
                      <CheckCircle size={14} />{' '}
                      {t('dataBreaches.details.notified', { count: breach.users_notified_count })}
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showForm && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="data-breach-dialog-title"
            onKeyDown={handleFormKeyDown}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-sm animate-in fade-in duration-300 sm:p-4"
          >
            <Card className="max-h-[90vh] w-full max-w-2xl overflow-y-auto shadow-elevated">
              <div className="space-y-5 p-4 sm:space-y-6 sm:p-8">
                <div className="flex items-center justify-between gap-3">
                  <h2
                    id="data-breach-dialog-title"
                    className="flex items-center gap-2 text-xl font-bold tracking-tight text-state-danger-light dark:text-state-danger-dark sm:text-2xl"
                  >
                    <AlertOctagon aria-hidden="true" /> {t('dataBreaches.reportIncident')}
                  </h2>
                  <button
                    type="button"
                    aria-label={t('dataBreaches.form.close')}
                    onClick={closeReportForm}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-surface-2-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:hover:bg-surface-2-dark"
                  >
                    <X className="text-text-subtle-light dark:text-text-subtle-dark" />
                  </button>
                </div>

                <form onSubmit={handleCreateBreach} className="space-y-6">
                  {error && (
                    <p
                      role="alert"
                      className="rounded-lg border border-state-danger-light/20 bg-state-danger-bg-light p-3 text-sm text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                    >
                      {error}
                    </p>
                  )}
                  <Input
                    id="incident-title"
                    label={t('dataBreaches.form.title')}
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    autoFocus
                  />

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                      id="incident-discovery-date"
                      label={t('dataBreaches.form.discoveryDate')}
                      type="date"
                      value={formData.discovery_date}
                      onChange={(e) => setFormData({ ...formData, discovery_date: e.target.value })}
                      required
                    />
                    <div>
                      <label htmlFor="incident-severity" className="app-field-label">
                        {t('dataBreaches.form.severity')}
                      </label>
                      <select
                        id="incident-severity"
                        className="app-field-control min-h-11 w-full rounded-lg border border-surface-border-light bg-surface-card-light px-3 text-sm text-text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-text-primary-dark"
                        value={formData.severity}
                        onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                      >
                        {['low', 'medium', 'high', 'critical'].map((severity) => (
                          <option key={severity} value={severity}>
                            {t(`dataBreaches.severity.${severity}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                      id="incident-affected-users-count"
                      label={t('dataBreaches.form.affectedUsersCount')}
                      type="number"
                      min={0}
                      value={formData.affected_users_count}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          affected_users_count: Math.max(
                            0,
                            Number.parseInt(e.target.value, 10) || 0
                          ),
                        })
                      }
                    />
                    <Input
                      id="incident-affected-data-types"
                      label={t('dataBreaches.form.affectedDataTypes')}
                      placeholder={t('dataBreaches.form.affectedDataTypesPlaceholder')}
                      value={formData.affected_data_types.join(', ')}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          affected_data_types: e.target.value
                            .split(',')
                            .map((value) => value.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </div>

                  <label
                    htmlFor="incident-regulatory-filing"
                    className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-surface-border-light p-3 text-sm text-text-primary-light focus-within:ring-2 focus-within:ring-brand-600 dark:border-surface-border-dark dark:text-text-primary-dark"
                  >
                    <input
                      id="incident-regulatory-filing"
                      type="checkbox"
                      className="h-5 w-5 shrink-0 accent-brand-600"
                      checked={formData.regulatory_filing_required}
                      onChange={(e) =>
                        setFormData({ ...formData, regulatory_filing_required: e.target.checked })
                      }
                    />
                    {t('dataBreaches.form.regulatoryFilingRequired')}
                  </label>

                  <div>
                    <label htmlFor="incident-description" className="app-field-label">
                      {t('dataBreaches.form.description')}
                    </label>
                    <textarea
                      id="incident-description"
                      className="app-field-control min-h-28 w-full resize-y rounded-lg border border-surface-border-light bg-surface-card-light px-3 py-2.5 text-sm text-text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-text-primary-dark"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      maxLength={5000}
                    />
                  </div>

                  <div>
                    <label htmlFor="incident-impact-assessment" className="app-field-label">
                      {t('dataBreaches.form.impactAssessment')}
                    </label>
                    <textarea
                      id="incident-impact-assessment"
                      className="app-field-control min-h-24 w-full resize-y rounded-lg border border-surface-border-light bg-surface-card-light px-3 py-2.5 text-sm text-text-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-text-primary-dark"
                      value={formData.impact_assessment}
                      onChange={(e) =>
                        setFormData({ ...formData, impact_assessment: e.target.value })
                      }
                      required
                      maxLength={5000}
                    />
                  </div>

                  <div className="flex flex-col-reverse gap-3 border-t border-surface-border-light pt-4 dark:border-surface-border-dark sm:flex-row sm:justify-end">
                    <Button variant="secondary" onClick={closeReportForm} type="button">
                      {t('dataBreaches.form.cancel')}
                    </Button>
                    <Button
                      className="bg-state-danger-light dark:bg-state-danger-dark hover:opacity-90 text-white"
                      type="submit"
                      isLoading={submitting}
                    >
                      {t('dataBreaches.form.submit')}
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
